import React, {useRef, useState} from 'react';

import FormGroup from 'ui-library/bs/FormGroup';
import FieldError from 'ui-library/FieldError';
import useOnMount from 'ui-library/hooks/useOnMount';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {Size, TextInputType} from 'ui-library/inputs/InputConstants';
import TextInput from 'ui-library/inputs/TextInput';
import Modal from 'ui-library/modals/Modal';

interface RenameTableModalProps {
  initialName: string;
  setNameAndClose: (name: string | null) => void;
  isNameUsed: (name: string) => boolean;
  maxLength: number;
  title: string;
}

const NAME_INPUT_ID = 'RenameNoteLinkedObjectModal-name';

const RenameNoteLinkedObjectModal = (props: RenameTableModalProps): JSX.Element => {
  const [name, setName] = useState(props.initialName);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const getNameErrorMessage = (name: string): string | null => {
    if (name.length > props.maxLength) {
      return `Name exceeds ${props.maxLength} characters`;
    }
    if (props.isNameUsed(name) && name !== props.initialName) {
      return 'Name already in use';
    }
    return null;
  };

  const handleSubmit = (): void => {
    const error = getNameErrorMessage(name);
    if (error == null) {
      if (name === props.initialName) {
        // User did not change the table name
        props.setNameAndClose(null);
      } else {
        props.setNameAndClose(name);
      }
    } else {
      setErrorMessage(error);
      focus();
    }
  };

  const handleClose = (): void => {
    props.setNameAndClose(null);
  };

  const focus = (): void => {
    const textInput = inputRef.current!;
    textInput.focus();
    textInput.select();
  };

  useOnMount(focus);

  return (
    <Modal
      title={props.title}
      onRequestClose={handleClose}
      onRequestSubmit={handleSubmit}
      buttons={[{text: 'Save', submit: true, disabled: name.length === 0}]}
    >
      <FormGroup>
        <FieldLabel label="Name" htmlFor={NAME_INPUT_ID} />
        <TextInput
          id={NAME_INPUT_ID}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          autoFocus={true}
          hasError={errorMessage != null}
          hasButton={false}
          size={Size.SMALL}
          type={TextInputType.TEXT}
          inputRef={inputRef}
        />
        <FieldError compact={true} message={errorMessage} />
      </FormGroup>
    </Modal>
  );
};

export default RenameNoteLinkedObjectModal;
